import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { dataConstants } from "../../../constants";
import { colors, radii } from "../../../utils/designTokens";

const { PLAN_QANDA } = dataConstants || {};

/* ── Link replacements for FAQ answers ────────────────────────────────────── */

const replacements = [
  { tag: '@grufityreports', link: 'https://twitter.com/grufityreports' },
  { tag: '@grufity', link: 'https://twitter.com/grufity?lang=en' },
  { tag: 'support@grufity.com', link: 'mailto:support@grufity.com?subject=Regarding%20Pricing' },
  { tag: 'https://www.grufity.com/docs/api', link: 'https://www.grufity.com/docs/api' },
];

const replaceTagsWithLinks = (inputText, replacementList) => {
  return replacementList.reduce((result, { tag, link }) => {
    const lowercaseTag = tag.toLowerCase();
    const regex = new RegExp(`(?<!\\w)${lowercaseTag}(?!\\w)`, 'gi');
    return result.replace(regex, `<a href="${link.toLowerCase()}" target="_blank" rel="noreferrer noopener nofollow">${tag}</a>`);
  }, inputText);
};

/* ── Styled Accordion ─────────────────────────────────────────────────────── */

const Accordion = styled(MuiAccordion)(() => ({
  boxShadow: 'none',
  border: `1px solid ${colors.neutral[100]}`,
  borderRadius: `${radii.md}px !important`,
  marginBottom: 8,
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    border: `1px solid ${colors.neutral[200]}`,
    margin: '0 0 8px 0',
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(() => ({
  backgroundColor: '#ffffff',
  borderRadius: `${radii.md}px`,
  minHeight: 52,
  '&.Mui-expanded': {
    minHeight: 52,
    borderBottom: `1px solid ${colors.neutral[100]}`,
    backgroundColor: colors.neutral[25],
    borderRadius: `${radii.md}px ${radii.md}px 0 0`,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '12px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: '16px 20px',
}));

/* ── Component ────────────────────────────────────────────────────────────── */

const FAQs = ({ pricePlans }) => {
  const plan = pricePlans?.length > 0
    ? pricePlans.find(d => d.type === 'PREMIUM')
    : null;
  const qAndA = plan?.features
    ? JSON.parse(plan.features).qAndA
    : PLAN_QANDA;

  if (!qAndA?.length) return null;

  return (
    <Box
      id="faq"
      sx={{
        scrollMarginTop: '80px',
        py: { xs: 6, md: 8 },
        borderTop: `1px solid ${colors.neutral[200]}`,
      }}
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      {/* Section heading */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
        <Typography
          variant="overline"
          component="div"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            color: colors.accent.main,
            mb: 1.5,
          }}
        >
          FAQ
        </Typography>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.4rem', md: '1.75rem' },
            color: colors.neutral[900],
            mb: 1.5,
          }}
        >
          Frequently Asked Questions
        </Typography>
      </Box>

      {/* Accordion list */}
      <Box sx={{ maxWidth: 680, mx: 'auto' }}>
        {qAndA.map((qa, ind) =>
          qa.a && qa.a !== '' ? (
            <Accordion
              key={ind}
              defaultExpanded
              itemProp="mainEntity"
              itemScope
              itemType="https://schema.org/Question"
            >
              <AccordionSummary
                aria-controls={`question${ind}-content`}
                id={`question${ind}-header`}
                expandIcon={<ExpandMoreRoundedIcon sx={{ color: colors.accent.main }} />}
              >
                <Typography
                  variant="body2"
                  component="h3"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.7,
                    color: colors.neutral[800],
                  }}
                  dangerouslySetInnerHTML={{ __html: qa.q }}
                  itemProp="name"
                />
              </AccordionSummary>
              <AccordionDetails itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    lineHeight: 1.7,
                    color: colors.neutral[600],
                    '& a': { textDecoration: 'none', color: colors.accent.main },
                    '& a:hover': { textDecoration: 'underline' },
                  }}
                  dangerouslySetInnerHTML={{ __html: replaceTagsWithLinks(qa.a, replacements) }}
                  itemProp="text"
                />
              </AccordionDetails>
            </Accordion>
          ) : null
        )}
      </Box>
    </Box>
  );
};

export default FAQs;
